import type {
  MediaFileService,
  MediaFileUploadResponse,
} from '@ps/ui/upload/functions/MediaFileUploadController';
import { uploadMediaItem } from '@ps/ui/upload/functions/uploadMediaItem';
import type { UploadProgress } from '@ps/ui/upload/types/UploadProgress';

import type { ChannelMessageMediaMetadata } from '../../types/ChannelMessageMediaInfo';

/**
 * Per-file upload response surfaced to the `MediaFileUploadController`
 * (and downstream to the message provider) after a channel-message
 * media upload completes. Mirrors PopshopRN's
 * `GroupMessageMediaFileUploadResponse` shape but trimmed to what the
 * web actually consumes.
 */
export interface ChannelMessageMediaUploadResponse extends MediaFileUploadResponse {
  filename: string;
  contentType: string;
  metadata?: ChannelMessageMediaMetadata;
}

/**
 * `MediaFileService` impl that uploads a single channel-message
 * attachment via the media-gallery presigned-URL pipeline.
 * Mirrors PopshopRN's `useCommunityChannelMessageMediaFileService`
 * but as a plain factory — the React lifecycle lives on the bridge
 * hook (`useChannelMessageMediaUploader`).
 *
 * Wires the right category (`popStoreGroupMessages`) and object tag
 * (`com.popstore.community.group_message.image|video`) so the
 * server stores the upload under the right pipeline.
 */
export function channelMessageMediaFileService(
  creatorId: string,
): MediaFileService<ChannelMessageMediaUploadResponse> {
  return {
    upload: async params => {
      if (!params.localFile) {
        throw new Error('channelMessageMediaFileService requires a File');
      }
      const isImage = params.contentType.toLowerCase().startsWith('image/');
      const tag = isImage
        ? 'com.popstore.community.group_message.image'
        : 'com.popstore.community.group_message.video';
      const caption = params.filename.replace(/\.[^.]+$/, '');
      const result = await uploadMediaItem({
        file: params.localFile,
        category: 'popStoreGroupMessages',
        metadata: {
          tag,
          caption,
          createdBy: creatorId,
        },
        signal: params.abortController.signal,
        onProgress: (progress: UploadProgress) => {
          params.onProgress(progress.ratio);
        },
      });
      if (result.id == null) {
        throw new Error('Upload completed without a media id');
      }
      return {
        id: result.id,
        url: result.url,
        filename: result.name,
        contentType: result.type,
        metadata: result.metadata
          ? {
              blurhash: result.metadata.blurhash,
              brightness: result.metadata.brightness,
              dimensions:
                result.metadata.width != null && result.metadata.height != null
                  ? {
                      width: result.metadata.width,
                      height: result.metadata.height,
                    }
                  : undefined,
            }
          : undefined,
      };
    },
  };
}
